// app/javascript/controllers/map_controller.js
//
// Leaflet map for pinning a location.
// - Initial center comes from the `latitude` / `longitude` values.
// - Two button actions: `recenter` (back to the initial center) and
//   `getUserLocation` (device GPS).
import { Controller } from "@hotwired/stimulus"
import L from "leaflet"

export default class extends Controller {
  static targets = ["container", "lat", "lng", "latInput", "lngInput", "submitBtn", "toast"]
  static values = {
    markerIcon: String,
    markerIcon2x: String,
    markerShadow: String,
    latitude: Number,
    longitude: Number
  }
  
  connect() {
    this.configureLeafletIcons()
    this.initializeMap()
  }

  configureLeafletIcons() {
    // Fix Leaflet's default icon paths using Rails asset pipeline URLs
    delete L.Icon.Default.prototype._getIconUrl
    L.Icon.Default.mergeOptions({
      iconRetinaUrl: this.markerIcon2xValue,
      iconUrl: this.markerIconValue,
      shadowUrl: this.markerShadowValue,
    })
  }

  initializeMap() {
    // Use backup coordinates if available, otherwise default
    const defaultLat = this.hasLatitudeValue && this.latitudeValue ? this.latitudeValue : 51.505
    const defaultLng = this.hasLongitudeValue && this.longitudeValue ? this.longitudeValue : -0.09
    
    // Initialize map on the container target
    this.map = L.map(this.containerTarget).setView([defaultLat, defaultLng], 13);
    
    L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
        maxZoom: 19
    }).addTo(this.map);

    this.currentMarker = null
    
    // If we have backup coordinates, show them as a marker
    if (this.hasLatitudeValue && this.hasLongitudeValue && this.latitudeValue && this.longitudeValue) {
      this.setCoordinates(this.latitudeValue, this.longitudeValue)
    }
    
    // Add click handler for pinning locations
    this.map.on('click', this.onMapClick.bind(this))
  }

  onMapClick(e) {
    const { lat, lng } = e.latlng
    
    this.setCoordinates(lat, lng)

  }
  
  enable_submit_btn() {
    this.submitBtnTarget.disabled = false
    this.submitBtnTarget.classList.remove('opacity-50')
  }

  updateCoordinates(lat, lng) {
    // Update coordinate display
    if (this.hasLatTarget) {
      this.latTarget.textContent = lat.toFixed(6)
    }
    if (this.hasLngTarget) {
      this.lngTarget.textContent = lng.toFixed(6)
    }
    
    // Update hidden form inputs for Rails
    if (this.hasLatInputTarget) {
      this.latInputTarget.value = lat
    }
    if (this.hasLngInputTarget) {
      this.lngInputTarget.value = lng
    }
    
    // Store for easy access
    this.selectedLat = lat
    this.selectedLng = lng
  }

  recenter() {
    this.setCoordinates(this.latitudeValue, this.longitudeValue)
  }

  getUserLocation() {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const lat = position.coords.latitude
          const lng = position.coords.longitude
          this.setCoordinates(lat, lng)
        },
        (error) => {
          this.showLocationToast()
        },
        { timeout: 5000 }
      )
    } else {
      this.showLocationToast()
    }
  }

  showLocationToast() {
    this.toastTarget.classList.remove('hidden')
    // Auto-hide after 6 seconds
    setTimeout(() => this.hideToast(), 6000)
  }

  hideToast() {
    this.toastTarget.classList.add('hidden')
  }

  setCoordinates(lat, lng) {
    if (this.currentMarker) {
      this.map.removeLayer(this.currentMarker)
    }
    
    this.currentMarker = L.marker([lat, lng]).addTo(this.map)
    
    this.map.setView([lat, lng])
    this.updateCoordinates(lat, lng)
    this.enable_submit_btn();
  }

  // Cleanup when controller disconnects
  disconnect() {
    if (this.map) {
      this.map.remove()
    }
  }
}